import { Component, Input } from '@angular/core';
import { AttributePanelState } from '../../../services/animation/animation.types';
import { AnimationService } from '../../animation/animation.service';
import { Store } from '@ngrx/store';
import { setAnimationConfig } from '../../store/editor.actions';
import { Observable } from 'rxjs';
import {
  selectAnimationConfigs,
  selectAnimationSidePanelState,
  selectedShapes,
} from '../../store/selector/editor.selector';
import { map } from 'rxjs/operators';
import {
  AnimationConfig,
  ContainerAnimationConfig,
  ContainerTransition,
  TextAnimationConfig,
} from '../../../elements/resource/types/shape.type';

export interface AnimationSidePanelState {
  IRI?: string;
  type: 'multi-selection' | 'container' | 'item' | 'text';
  appear?: AnimationConfig;
  disappear?: AnimationConfig;
}

@Component({
  selector: 'nw-animation-side-panel',
  templateUrl: './animation-side-panel.component.html',
  styleUrls: ['./animation-side-panel.component.scss'],
})
export class AnimationSidePanelComponent {
  @Input() state: AttributePanelState;

  areThereSelectedShapes$: Observable<boolean>;
  animationSidePanelState$: Observable<AnimationSidePanelState>;
  selectedShapes$: Observable<string[]>;
  appearAnimationConfig$: Observable<any>;
  appearDefined$: Observable<boolean>;
  appearTypeValues$: Observable<string[]>;
  isFadeSelected$: Observable<boolean>;
  isMaskSelected$: Observable<boolean>;

  onlyAppearValue$: Observable<string>;
  motionValues$: Observable<string[]>;

  // Motion values
  isTextShapePresent$: Observable<boolean>;

  byValues$: Observable<string[]>;
  isByLetter$: Observable<boolean>;
  isByWord$: Observable<boolean>;
  isByLine$: Observable<boolean>;

  animationConfig$: Observable<AnimationConfig>;

  appearTextAnimation$: Observable<TextAnimationConfig>;
  appearModeAnimation$: Observable<ContainerAnimationConfig>;
  disappearTextAnimation$: Observable<TextAnimationConfig>;
  disappearContainerAnimation$: Observable<ContainerAnimationConfig>;
  currentIRI: string;
  constructor(
    public readonly animationsService: AnimationService,
    private readonly store: Store,
  ) {
    this.animationSidePanelState$ = this.store.select(
      selectAnimationSidePanelState,
    );

    this.animationSidePanelState$.subscribe(val => {
      // console.log('animation-panel-state', val);
      this.currentIRI = val?.IRI;
    });
    this.appearTextAnimation$ = this.animationSidePanelState$.pipe(
      map(state => {
        if (state.type == 'text') {
          return state.appear as TextAnimationConfig;
        }
      }),
    );

    this.appearModeAnimation$ = this.animationSidePanelState$.pipe(
      map(state => {
        if (!state) {
          return;
        }
        if (state.type == 'container' || state.type == 'text') {
          return state.appear as ContainerAnimationConfig;
        }
      }),
    );

    this.disappearTextAnimation$ = this.animationSidePanelState$.pipe(
      map(state => {
        if (state.type == 'text') {
          return state.disappear as TextAnimationConfig;
        }
      }),
    );

    this.disappearContainerAnimation$ = this.animationSidePanelState$.pipe(
      map(state => {
        if (state.type == 'container') {
          return state.disappear as ContainerAnimationConfig;
        }
      }),
    );

    this.animationSidePanelState$.subscribe(val => {
      this.currentIRI = val?.IRI;
    });

    // -- // -- //
    this.areThereSelectedShapes$ = this.store
      .select(selectedShapes)
      .pipe(map(shapes => shapes.length > 0));

    this.selectedShapes$ = this.store.select(selectedShapes);
    this.appearAnimationConfig$ = this.store.select(
      selectAnimationConfigs('appear'),
    );

    this.isTextShapePresent$ = this.appearAnimationConfig$.pipe(
      map(config => config.textShapePresent),
    );

    this.appearDefined$ = this.appearAnimationConfig$.pipe(
      map(config => Object.keys(config || {}).length > 0),
    );

    this.appearTypeValues$ = this.appearAnimationConfig$.pipe(
      map(config => Object.keys(config.type || {})),
    );

    this.motionValues$ = this.appearAnimationConfig$.pipe(
      map(config => Object.keys(config.motion || {})),
    );

    this.motionValues$.subscribe(motionvalues => {
      // console.log('motionvalues', motionvalues),
    });

    this.byValues$ = this.appearAnimationConfig$.pipe(
      map(config => Object.keys(config.by || {})),
    );

    this.isFadeSelected$ = this.appearTypeValues$.pipe(
      map(values => values.includes('fade')),
    );

    this.isMaskSelected$ = this.appearTypeValues$.pipe(
      map(values => values.includes('mask')),
    );

    this.appearAnimationConfig$.subscribe(val => {
      // console.log('appearAnimationConfig', val);
    });

    this.isByWord$ = this.byValues$.pipe(
      map(values => values.includes('word')),
    );

    this.isByLetter$ = this.byValues$.pipe(
      map(values => values.includes('letter')),
    );

    this.isByLine$ = this.byValues$.pipe(
      map(values => values.includes('line')),
    );
  }
  addAppearAnimation() {
    this.store.dispatch(
      setAnimationConfig({
        baseType: 'appear',
        animationType: 'fade',
      }),
    );
  }

  selectFloatAppearMode(float: AnimationConfig['float']) {
    this.store.dispatch(
      setAnimationConfig({
        IRI: this.currentIRI,
        baseType: 'appear',
        float,
      }),
    );
  }

  selectScaleMode(scale: AnimationConfig['scale']) {
    this.store.dispatch(
      setAnimationConfig({
        IRI: this.currentIRI,
        baseType: 'appear',
        scale,
      }),
    );
  }

  selectContainerTransition(mode: ContainerTransition) {
    this.store.dispatch(
      setAnimationConfig({
        IRI: this.currentIRI,
        baseType: 'appear',
        mode,
      }),
    );
  }

  selectTextAnimationBy(by: TextAnimationConfig['by']) {
    this.store.dispatch(
      setAnimationConfig({
        IRI: this.currentIRI,
        baseType: 'appear',
        by,
      }),
    );
  }

  removeAppearAnimation() {
    this.store.dispatch(
      setAnimationConfig({
        baseType: 'appear',
      }),
    );
  }
  saveType(type: 'fade' | 'mask') {
    this.store.dispatch(
      setAnimationConfig({
        baseType: 'appear',
        animationType: type,
      }),
    );
  }
  setMotion(
    key: 'float' | 'scale',
    value: 'top' | 'bottom' | 'right' | 'left',
  ) {
    console.log('set-motion', key, value);
    this.store.dispatch(
      setAnimationConfig({
        baseType: 'appear',
        animationType: 'fade',
        [key]: value,
      }),
    );
  }

  setTextBy(by: 'word' | 'letter' | 'line') {
    this.store.dispatch(
      setAnimationConfig({
        baseType: 'appear',
        by,
      }),
    );
  }

  resetMotion() {
    this.store.dispatch(
      setAnimationConfig({
        baseType: 'appear',
        animationType: 'fade',
        float: null,
        scale: null,
      }),
    );
  }

  playSlidePreview() {
    this.animationsService.playSlide();
  }
}
